import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, success, warning,
  surfaceRaised, easing,
} from "../../layouts/schoolPortal/schoolPortalTheme";
import { formatMoney, regionOf } from "../../utils/region";
import { formatSubjectName } from "../../layouts/schoolPortal/schoolPortalTheme";
import BookingActions from "./Tutors/BookingActions";
import { useSchool } from "../../Hooks/SchoolContext";

/**
 * The three school-wide questions that could only be answered one student at a
 * time: is the work being done, what is in the diary, and what have we earned.
 *
 * WHY THESE THREE
 * Everything a school portal knows about homework and sessions was locked
 * inside individual records. An owner wanting to know whether the work their
 * instructors set was being touched had to open forty pages, which means
 * nobody ever did — so the product could set work and never report on it.
 *
 * PROGRESS IS COUNTED, NOT REPORTED
 * Every figure here is real answers against a task's own target since the
 * moment it was set. Nobody ticks anything, and a task that cannot be measured
 * says so rather than showing an empty bar that will never move.
 */

const dateLabel = (d) => {
  const at = new Date(d);
  const days = Math.round((at - Date.now()) / 864e5);
  const time = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (days <= 0) return `Today ${time}`;
  if (days === 1) return `Tomorrow ${time}`;
  return `${at.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} ${time}`;
};

function Panel({ title, action, children }) {
  return (
    <Box sx={{
      backgroundColor: "#fff", border: `1px solid ${border}`, borderRadius: "10px",
      px: 1.75, py: 1.5, display: "flex", flexDirection: "column", minWidth: 0,
    }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1 }}>
        <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 800, color: textPrimary }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/** A count with its label, sized so the number is the thing you read. */
function Figure({ value, label, color = textPrimary, title }) {
  const body = (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: font, fontSize: "19px", fontWeight: 800, color, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary, lineHeight: 1.3 }}>
        {label}
      </Typography>
    </Box>
  );
  return title ? <Tooltip title={title} arrow>{body}</Tooltip> : body;
}

function WorkPanel({ work, navigate }) {
  const t = work?.totals || { open: 0 };
  const rows = work?.rows || [];

  return (
    <Panel title="Work set">
      {!t.open ? (
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted, lineHeight: 1.5 }}>
          Nothing outstanding across the school. Work set with a target — so many
          questions on a subject — reports its own progress here.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2.5, mb: 1.25, flexWrap: "wrap" }}>
            <Figure value={t.open} label={`open · ${t.studentsWithWork} student${t.studentsWithWork === 1 ? "" : "s"}`} />
            <Figure
              value={t.stalled} label="never started"
              color={t.stalled ? warning : textPrimary}
              title="Set five days ago or more, with not one answer against it"
            />
            <Figure value={t.overdue} label="overdue" color={t.overdue ? warning : textPrimary} />
            <Figure value={t.finished} label="finished" color={t.finished ? success : textPrimary} />
          </Box>

          {/* THE HONEST CAVEAT, ONCE.
              A task with no target can never close itself and will never show
              progress. When most of a school's homework is like that, the panel
              above is measuring almost nothing, and saying so is the difference
              between a dashboard and a decoration. */}
          {t.unmeasurable > 0 && (
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mb: 1, lineHeight: 1.45 }}>
              {t.unmeasurable === t.open
                ? "None of it has a target, so none of it can report progress."
                : `${t.unmeasurable} of these have no target, so they cannot report progress.`}
            </Typography>
          )}

          <Box sx={{ borderTop: `1px solid ${border}` }}>
            {rows.slice(0, 6).map((r) => {
              const p = r.progress;
              const measurable = Boolean(p && p.required);
              return (
                <Box
                  key={r.id}
                  onClick={() => navigate(`/school/students/${r.studentId}`)}
                  sx={{
                    py: 0.85, borderBottom: `1px solid ${border}`, cursor: "pointer",
                    "&:last-of-type": { borderBottom: "none" },
                    "&:hover": { backgroundColor: surfaceRaised },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Typography noWrap sx={{ fontFamily: font, fontSize: "12px", fontWeight: 600, color: textPrimary, flexShrink: 0, maxWidth: "40%" }}>
                      {r.studentName}
                    </Typography>
                    <Typography noWrap sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, flex: 1, minWidth: 0 }}>
                      {r.title}
                    </Typography>
                    <Typography sx={{
                      fontFamily: font, fontSize: "11.5px", fontWeight: 700, flexShrink: 0,
                      fontVariantNumeric: "tabular-nums",
                      color: r.stalled ? warning : p?.met ? success : textSecondary,
                    }}>
                      {measurable ? `${p.answered}/${p.required}` : "—"}
                    </Typography>
                  </Box>
                  {measurable && (
                    <Box sx={{ height: 3, borderRadius: 2, backgroundColor: surfaceRaised, mt: 0.4, overflow: "hidden" }}>
                      <Box sx={{
                        width: `${Math.max(p.percent ? 2 : 0, p.percent)}%`, height: "100%",
                        backgroundColor: p.met ? success : r.stalled ? warning : accent,
                        transition: `width 0.3s ${easing}`,
                      }} />
                    </Box>
                  )}
                  <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: r.overdue ? warning : textMuted, mt: 0.2 }}>
                    {[
                      r.stalled ? "never started" : null,
                      r.overdue ? "overdue" : r.dueAt ? `due ${new Date(r.dueAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : null,
                      r.setDaysAgo != null ? `set ${r.setDaysAgo}d ago` : null,
                    ].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {rows.length > 6 && (
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 0.75 }}>
              and {rows.length - 6} more
            </Typography>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * One session in the diary — and, for a 1:1, everything you would do to it.
 *
 * WHY A PAID SESSION LOOKS DIFFERENT
 * A slot held for an answer nobody has given and a session a student has
 * agreed to and paid for were the same grey line. They are not the same
 * thing: one is a hope and the other is the only outcome this whole path
 * exists to produce. A confirmed, paid session gets the accent rail and says
 * so; one accepted in the last two days says that too, because it is news.
 *
 * WHY CANCEL IS BEHIND A CLICK
 * Cancelling moves money — it voids or refunds depending on how close the
 * session is, and the policy service decides which. Putting that beside a row
 * you might click to open a student is how somebody cancels a lesson they
 * meant to read. BookingActions already owns reschedule, cancel and no-show,
 * shows the refund quote before it acts, and is reused here rather than
 * reimplemented so both surfaces cancel the same way.
 */
function BookingRow({ row: r, region, navigate, onChanged, canTeach, isManager }) {
  const [open, setOpen] = React.useState(false);
  const isSession = r.kind !== "group";
  const paid = Boolean(r.paid);
  const justAccepted = r.confirmedAt && Date.now() - new Date(r.confirmedAt).getTime() < 2 * 864e5;

  // BookingActions reads five fields off a booking. The diary carries a
  // summary, so the shape it needs is rebuilt rather than refetched.
  const booking = isSession ? {
    _id: r.bookingId,
    start_time: r.at,
    end_time: new Date(new Date(r.at).getTime() + (r.minutesLong || r.minutes || 60) * 60000).toISOString(),
    status: r.status,
    subject: r.subject,
  } : null;

  return (
    <Box sx={{
      borderBottom: `1px solid ${border}`, "&:last-of-type": { borderBottom: "none" },
      borderLeft: paid ? `3px solid ${success}` : "3px solid transparent",
      pl: paid ? 1 : 0, ml: paid ? -0.5 : 0,
      backgroundColor: justAccepted ? "rgba(22,163,74,0.05)" : "transparent",
    }}>
      <Box
        onClick={() => (isSession ? setOpen((o) => !o) : null)}
        sx={{
          display: "flex", alignItems: "baseline", gap: 1, py: 0.85,
          cursor: isSession ? "pointer" : "default",
          "&:hover": { backgroundColor: isSession ? surfaceRaised : "transparent" },
        }}
      >
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, width: 112, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
          {dateLabel(r.at)}
        </Typography>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: paid ? 700 : 500, color: textPrimary }}>
            {r.kind === "group"
              ? `${r.title}${r.capacity ? ` · ${r.booked}/${r.capacity}` : ""}`
              : r.studentName}
          </Typography>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
            {[
              r.subject ? formatSubjectName(r.subject) : null,
              r.minutesLong || r.minutes ? `${r.minutesLong || r.minutes}m` : null,
              r.instructorName,
            ].filter(Boolean).join(" · ")}
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          {r.awaiting ? (
            <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, color: warning }}>
              awaiting {r.awaiting}
            </Typography>
          ) : paid ? (
            <>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 800, color: success, fontVariantNumeric: "tabular-nums" }}>
                {formatMoney(r.price, region, { decimals: Number.isInteger(Number(r.price)) ? 0 : 2 })}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "10px", color: justAccepted ? success : textMuted, fontWeight: justAccepted ? 700 : 400 }}>
                {justAccepted ? "just accepted" : "paid"}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>

      {isSession && open && (
        <Box sx={{ pb: 1.25, pl: 0.25 }}>
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mb: 0.75, lineHeight: 1.5 }}>
            {paid
              ? "Their card is authorised and is captured after the session. Cancelling releases it — how much depends on how close the session is."
              : "Nothing has been charged for this yet."}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              onClick={() => r.studentId && navigate(`/school/students/${r.studentId}`)}
              sx={{
                fontFamily: font, fontSize: "11.5px", fontWeight: 700, color: accent, cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Open {r.studentName}
            </Typography>
            {/* canTeach is NOT hard-coded true. It decides what the reschedule
                picker says when it finds no slots: /my-slots builds them from
                the CALLER's own working hours, so telling a receptionist to fix
                their availability is advice for a problem they do not have.
                isManager makes the cancel dialog say you are acting on the
                instructor's behalf — which, on a school-wide dashboard, is
                usually exactly what is happening. The server decides who may
                actually cancel: the assigned instructor, or a manager at the
                booking's own school. Anyone else gets a 404. */}
            <BookingActions booking={booking} canTeach={canTeach} isManager={isManager} onDone={onChanged} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function DiaryPanel({ bookings, navigate, region, onChanged, canTeach, isManager }) {
  const t = bookings?.totals || { ahead: 0, days: 14 };
  const rows = bookings?.rows || [];

  return (
    <Panel
      title="The diary"
      action={
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
          next {t.days} days
        </Typography>
      }
    >
      {!t.ahead ? (
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted, lineHeight: 1.5 }}>
          Nothing booked in the next {t.days} days.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2.5, mb: 1.25, flexWrap: "wrap" }}>
            <Figure value={t.confirmed} label="confirmed" color={success} />
            <Figure value={t.next7} label="in the next 7 days" />
            {/* The only good news this product generates on its own, and it
                used to arrive in complete silence. */}
            {t.justAccepted > 0 && (
              <Figure value={t.justAccepted} label="accepted in the last 2 days" color={success}
                title="A student said yes to a proposed time and paid for it" />
            )}
            {/* A slot held for an answer nobody has given is not a booking, and
                a week that counts them looks full and turns out empty. */}
            {t.awaitingStudent > 0 && (
              <Figure value={t.awaitingStudent} label="waiting on a student" color={warning}
                title="Proposed, slot held, no answer yet" />
            )}
            {t.awaitingInstructor > 0 && (
              <Figure value={t.awaitingInstructor} label="waiting on an instructor" color={warning} />
            )}
          </Box>

          <Box sx={{ borderTop: `1px solid ${border}` }}>
            {rows.slice(0, 6).map((r) => (
              <BookingRow
                key={r.bookingId || r.groupSessionId}
                row={r}
                region={region}
                navigate={navigate}
                onChanged={onChanged}
                canTeach={canTeach}
                isManager={isManager}
              />
            ))}
          </Box>

          {t.ahead > 6 && (
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 0.75 }}>
              and {t.ahead - 6} more
            </Typography>
          )}
        </>
      )}
    </Panel>
  );
}

function RevenuePanel({ revenue, region }) {
  if (!revenue) return null;
  const money = (n) => formatMoney(n, region, { decimals: 0 });
  const b = revenue.breakdown || {};

  return (
    <Panel
      title="Revenue"
      action={
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
          since {new Date(revenue.since).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
        </Typography>
      }
    >
      <Typography sx={{ fontFamily: font, fontSize: "28px", fontWeight: 800, color: textPrimary, lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}>
        {money(revenue.total)}
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mb: 1.25 }}>
        earned in total · {money(revenue.thisMonth)} this month
      </Typography>

      {/* Where it came from. */}
      <Box sx={{ borderTop: `1px solid ${border}`, pt: 0.75 }}>
        {[
          ["1:1 sessions", b.oneToOne],
          ["Group sessions", b.group],
        ].map(([label, value]) => (
          <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.35 }}>
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>{label}</Typography>
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: value ? textPrimary : textMuted, fontVariantNumeric: "tabular-nums" }}>
              {money(value || 0)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 0.75 }}>
        {revenue.sessionsDelivered} session{revenue.sessionsDelivered === 1 ? "" : "s"} delivered
      </Typography>
    </Panel>
  );
}

// Mirrors TUTOR_CAPABLE_ROLES and canManageTutorsSettings on the backend.
// `operations` (receptionist) is deliberately absent from both: front desk
// only, never a tutor.
const TUTOR_CAPABLE = ["instructor", "primary_admin", "staff_admin"];
const MANAGER_ROLES = ["primary_admin", "staff_admin"];

export default function DashboardPulse({ pulse, onChanged, role = null }) {
  const navigate = useNavigate();
  const { school } = useSchool();
  if (!pulse) return null;
  const region = regionOf(school);

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: pulse.revenue ? "minmax(0,1.15fr) minmax(0,1fr) 260px" : "1fr 1fr" },
      gap: 1.5, mt: 1.5,
    }}>
      <WorkPanel work={pulse.work} navigate={navigate} />
      <DiaryPanel
        bookings={pulse.bookings}
        navigate={navigate}
        region={region}
        onChanged={onChanged}
        canTeach={TUTOR_CAPABLE.includes(role)}
        // Acting on someone else's booking is the norm on a school-wide view,
        // and the cancel dialog says so. An instructor looking at a diary
        // scoped to their own students is not.
        isManager={MANAGER_ROLES.includes(role) && pulse.scope !== "instructor"}
      />
      <RevenuePanel revenue={pulse.revenue} region={region} />
    </Box>
  );
}
